import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Send, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { Overline, ease } from "@/components/Reveal";
import { openWhatsApp, quoteSummaryMessage } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  vehicleType: "", origin: "", destination: "", timing: "", date: "",
  name: "", phone: "", email: "", notes: "", condition: "", canLoad: "",
};

const inputCls =
  "w-full rounded-md border border-edge bg-carbon px-4 py-3 text-sm text-ice placeholder:text-steel/60 focus:border-amber focus:outline-none focus:ring-1 focus:ring-amber transition-colors";

export function QuoteForm() {
  const { t, lang } = useLang();
  const q = t.quote;
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!data.vehicleType;
    if (step === 1) return data.origin.trim() && data.destination.trim();
    if (step === 2) return !!data.timing;
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error(q.errRequired);
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!data.name.trim() || !data.phone.trim()) {
      toast.error(q.errPhone);
      return;
    }
    if (!consent) {
      toast.error(q.errPrivacy);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, { ...data, privacyConsent: true, lang });
      setDone(true);
      toast.success(q.success);
    } catch (e) {
      toast.error(q.errRequired);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setData(empty); setConsent(false); setStep(0); setDone(false);
  };

  const stepTitles = [q.s1, q.s2, q.s3, q.s4];
  const progress = done ? 100 : ((step + 1) / 4) * 100;

  return (
    <section id="preventivo" data-testid="quote-section" className="relative bg-asphalt py-20 md:py-28">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <div className="mb-10 text-center">
          <Overline className="justify-center">{q.overline}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl lg:text-5xl">{q.title}</h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-steel">{q.subtitle}</p>
        </div>

        <div className="rounded-2xl border border-edge bg-carbon p-6 md:p-8" data-testid="quote-form-card">
          {/* progress bar */}
          <div className="mb-8">
            <div className="mb-2 flex justify-between text-xs font-semibold uppercase tracking-wide text-steel">
              <span>{done ? "✓" : `${step + 1} / 4`}</span>
              <span className="text-amber">{done ? "" : stepTitles[step]}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-edge">
              <motion.div className="h-full bg-amber" animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease }} />
            </div>
          </div>

          {done ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="py-8 text-center" data-testid="quote-success">
              <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber text-carbon">
                <Check className="h-8 w-8" strokeWidth={2.6} />
              </span>
              <h3 className="font-display text-2xl font-bold text-ice">{q.success}</h3>
              <p className="mx-auto mt-3 max-w-md text-sm text-steel">{q.note}</p>
              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => openWhatsApp(quoteSummaryMessage(data, lang))}
                  data-testid="quote-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-whatsapp px-6 py-3 font-display text-sm font-bold uppercase text-carbon transition-colors hover:bg-whatsapp/90"
                >
                  <MessageCircle className="h-4 w-4" /> {q.openWhatsapp}
                </button>
                <button type="button" onClick={reset} className="rounded-md border border-edge px-6 py-3 text-sm font-bold uppercase text-ice hover:border-amber">
                  {lang === "en" ? "New request" : "Nuova richiesta"}
                </button>
              </div>
            </motion.div>
          ) : (
            <>
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease }}>
                  {step === 0 && (
                    <div>
                      <h3 className="mb-4 font-display text-lg font-bold text-ice">{q.s1}</h3>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {q.vehicles.map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => set("vehicleType", v)}
                            data-testid={`quote-vehicle-${v}`}
                            className={`rounded-md border px-3 py-4 text-sm font-medium transition-all ${
                              data.vehicleType === v ? "border-amber bg-amber/10 text-amber" : "border-edge text-ice hover:border-steel"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-lg font-bold text-ice">{q.s2}</h3>
                      <div>
                        <label htmlFor="q-origin" className="mb-1.5 block text-sm text-steel">{q.origin}</label>
                        <input id="q-origin" data-testid="quote-origin" className={inputCls} value={data.origin} onChange={(e) => set("origin", e.target.value)} placeholder={q.origin} />
                      </div>
                      <div>
                        <label htmlFor="q-dest" className="mb-1.5 block text-sm text-steel">{q.destination}</label>
                        <input id="q-dest" data-testid="quote-destination" className={inputCls} value={data.destination} onChange={(e) => set("destination", e.target.value)} placeholder={q.destination} />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-lg font-bold text-ice">{q.s3}</h3>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {q.timings.map((tm) => (
                          <button
                            key={tm}
                            type="button"
                            onClick={() => set("timing", tm)}
                            data-testid={`quote-timing-${tm}`}
                            className={`rounded-md border px-3 py-3 text-sm font-medium transition-all ${
                              data.timing === tm ? "border-amber bg-amber/10 text-amber" : "border-edge text-ice hover:border-steel"
                            }`}
                          >
                            {tm}
                          </button>
                        ))}
                      </div>
                      <div>
                        <label htmlFor="q-date" className="mb-1.5 block text-sm text-steel">{q.timings[1]}</label>
                        <input id="q-date" type="date" data-testid="quote-date" className={inputCls} value={data.date} onChange={(e) => set("date", e.target.value)} />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-lg font-bold text-ice">{q.s4}</h3>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="q-name" className="mb-1.5 block text-sm text-steel">{q.name} *</label>
                          <input id="q-name" data-testid="quote-name" className={inputCls} value={data.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
                        </div>
                        <div>
                          <label htmlFor="q-phone" className="mb-1.5 block text-sm text-steel">{q.phone} *</label>
                          <input id="q-phone" type="tel" inputMode="tel" data-testid="quote-phone" className={inputCls} value={data.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="q-email" className="mb-1.5 block text-sm text-steel">{q.emailOpt}</label>
                        <input id="q-email" type="email" inputMode="email" data-testid="quote-email" className={inputCls} value={data.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="q-cond" className="mb-1.5 block text-sm text-steel">{q.condition}</label>
                          <input id="q-cond" data-testid="quote-condition" className={inputCls} value={data.condition} onChange={(e) => set("condition", e.target.value)} />
                        </div>
                        <div>
                          <label htmlFor="q-load" className="mb-1.5 block text-sm text-steel">{q.canLoad}</label>
                          <select id="q-load" data-testid="quote-canload" className={inputCls} value={data.canLoad} onChange={(e) => set("canLoad", e.target.value)}>
                            <option value="">—</option>
                            <option value={q.yes}>{q.yes}</option>
                            <option value={q.no}>{q.no}</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="q-notes" className="mb-1.5 block text-sm text-steel">{q.notes}</label>
                        <textarea id="q-notes" rows={2} data-testid="quote-notes" className={inputCls} value={data.notes} onChange={(e) => set("notes", e.target.value)} />
                      </div>

                      {/* summary */}
                      <div className="rounded-md border border-edge bg-asphalt/60 p-4 text-sm">
                        <p className="mb-2 font-display font-bold text-amber">{q.summary}</p>
                        <ul className="space-y-1 text-steel">
                          <li>{q.s1.replace("?", "")}: <span className="text-ice">{data.vehicleType || "—"}</span></li>
                          <li>{q.origin} → {q.destination}: <span className="text-ice">{data.origin || "—"} → {data.destination || "—"}</span></li>
                          <li>{q.s3.replace("?", "")}: <span className="text-ice">{data.timing || "—"}{data.date ? ` (${data.date})` : ""}</span></li>
                        </ul>
                      </div>

                      <label className="flex items-start gap-3 text-sm text-steel">
                        <input type="checkbox" data-testid="quote-privacy" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#F6B800]" />
                        <span>{q.privacy}</span>
                      </label>
                      <p className="text-xs text-steel/80">{q.note}</p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="quote-back"
                  className="inline-flex items-center gap-1 rounded-md px-4 py-2.5 text-sm font-bold uppercase text-steel transition-colors hover:text-ice disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" /> {t.common.back}
                </button>

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="quote-next"
                    className="inline-flex items-center gap-1 rounded-md bg-amber px-6 py-2.5 text-sm font-bold uppercase text-carbon transition-colors hover:bg-amber/90 active:scale-95"
                  >
                    {t.common.next} <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="quote-submit"
                    className="inline-flex items-center gap-2 rounded-md bg-amber px-6 py-2.5 text-sm font-bold uppercase text-carbon transition-colors hover:bg-amber/90 active:scale-95 disabled:opacity-70"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {t.common.send}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
